import { useNavigate, useSearchParams } from 'react-router';
import { useCallback, useEffect } from 'react';
import { WAITING, WELCOME_BACK_NEW } from 'Router/routes';
import { useApplicationQuery, useBankIDSessionsQuery } from 'hooks/webapi';
import useClientIdentificationStatusQuery from 'hooks/webapi/client/useIdentificationStatusQuery';
import { useTranslation } from 'react-i18next';
import type { IdentificationStatusProps } from './IdentificationStatus/types';
import getIdentificationStatusProps from './IdentificationStatus/getIdentificationStatusProps';

export default function useWelcomeBackIdentificationStatus() {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const { data: application, refetch: fetchApplication } =
    useApplicationQuery();
  const {
    data: identificationStatus,
    refetch: fetchIdentificationStatus,
    isLoading: identificationStatusIsLoading,
  } = useClientIdentificationStatusQuery();

  const [, setSearchParams] = useSearchParams();

  // SET IDENTIFICATION STATUSES TO URL QUERY FOR LOGS...
  useEffect(() => {
    if (identificationStatus) {
      setSearchParams({
        identityStatus: identificationStatus.identityStatus,
        bankAccountStatus: identificationStatus.bankAccountStatus,
      });
    }
  }, [identificationStatus, setSearchParams]);

  const translateStatus = useCallback(
    (statusObj?: IdentificationStatusProps) => {
      if (!statusObj) return undefined;

      const titleTranslationExists =
        statusObj.title && i18n.exists(statusObj.title);
      const descTranslationExists =
        statusObj.description && i18n.exists(statusObj.description);

      return {
        ...statusObj,
        title: titleTranslationExists ? t(statusObj.title) : undefined,
        description: descTranslationExists
          ? t(statusObj.description)
          : undefined,
      };
    },
    [t],
  );

  // Both conditions must be true before allowing the applicant to proceed to the waiting screen
  const bothStatusesAreApproved =
    identificationStatus?.identityStatus === 'APPROVED' &&
    identificationStatus?.bankAccountStatus === 'APPROVED_WITH_INCOME';

  // Condition to block client
  const isForbiddenIdentificationStatus =
    identificationStatus?.identityStatus === 'BANKID_FOREIGNER_NOT_ALLOWED' ||
    identificationStatus?.identityStatus === 'BANKID_NATIONALITY_BLACKLISTED';

  const bankIdSessionId = localStorage.getItem('bankIdSessionId');

  const enabledBankIDSessionRequest = Boolean(
    bankIdSessionId &&
      !isForbiddenIdentificationStatus &&
      !bothStatusesAreApproved,
  );

  const { data: bankIdSessionData } = useBankIDSessionsQuery(
    { uuid: bankIdSessionId! },
    enabledBankIDSessionRequest,
  );

  // Treat missing nationality as CZ (default assumption for domestic clients)
  const isCzNationality =
    !bankIdSessionData?.nationality || bankIdSessionData?.nationality === 'CZ';

  // Get correct translation for a given status (null when the status is missing)
  const getStatusProps = (
    type: 'identityStatus' | 'bankAccountStatus',
    status?: string,
    isCZResident?: boolean,
  ) =>
    status
      ? translateStatus(
          getIdentificationStatusProps(type, status, isCZResident),
        )
      : null;

  const identityStatusProps = getStatusProps(
    'identityStatus',
    identificationStatus?.identityStatus,
    isCzNationality,
  );

  const bankAccountStatusProps = getStatusProps(
    'bankAccountStatus',
    identificationStatus?.bankAccountStatus,
  );

  const handleBothApprovedClick = async () => {
    const isExpired = application
      ? application.expired
      : (await fetchApplication()).data?.expired;

    if (isExpired) {
      navigate(WELCOME_BACK_NEW);
      return;
    }

    navigate(WAITING);
  };

  return {
    identityStatusProps,
    bankAccountStatusProps,
    bothStatusesAreApproved,
    handleBothApprovedClick,
    isLoading: identificationStatusIsLoading,
    fetchStatus: fetchIdentificationStatus,
    isForbiddenIdentificationStatus,
  };
}
